import { concat, filter, find, first, isNumber, some } from 'es-toolkit/compat';
import { useParams } from 'react-router-dom';
import { NumberParam, UrlUpdateType, useQueryParam } from 'use-query-params';

import { getPrimaryRunOrchestrationIdFromExperimentResult } from 'components/Procedure/useSlideChannelsAndResults/secondaryAnalysis';
import { SlideWithChannelAndResults } from 'components/Procedure/useSlideChannelsAndResults/utils';
import {
  CalculateFeaturesJob,
  getSecondaryAnalysisJobPrimaryRunOrchestrationId,
  JobNotification,
  JobType,
  NucleaiExecutorJob,
} from 'interfaces/job';
import { Permission } from 'interfaces/permissionOption';
import { WebappOrchestration } from 'interfaces/webappOrchestration';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { usePermissions } from 'utils/usePermissions';
import { useRunSecondaryAnalysis } from './useRunSecondaryAnalysis';
import { useSecondaryAnalysisNotifications } from './useSecondaryAnalysisNotifications';
import {
  useSecondaryAnalysisOrchestrationIdQueryParams,
  useSecondaryAnalysisSelectionModeQueryParam,
} from './useSecondaryAnalysisQueryParams';

export interface SecondaryAnalysisContext {
  isSecondaryAnalysisActive: boolean;
  canRunSecondaryAnalysis: boolean;
  secondaryAnalysisActiveViewer: number;
  secondaryAnalysisSourceOrchestrationId: string;
  caseId: number;
  studyId: string;
  slideId: string;
  autoInternallyApprove: boolean;
  wholeSlideAnalysisJob: CalculateFeaturesJob | NucleaiExecutorJob;
  wholeSlideAnalysisJobType?: JobType;
  wholeSlideAnalysisJobName?: string;
  wholeSlideAnalysisJobDescription?: string;
  wholeSlideAnalysisJobParams?: CalculateFeaturesJob['params'];
  secondaryAnalysisNotifications?: JobNotification[];
  secondaryAnalysisAreaSelectionMode: string;
}

// We check if any experiment from the parent orchestration is approved / internally approved
const shouldAutoInternallyApprove = (
  slideForActiveViewer: SlideWithChannelAndResults,
  secondaryAnalysisSourceOrchestrationId: string
) => {
  const existingParentExperimentResult = find(slideForActiveViewer?.experimentResults, {
    orchestrationId: secondaryAnalysisSourceOrchestrationId,
  });
  const parentSecondaryExperimentResults = filter(
    slideForActiveViewer?.experimentResults,
    (result) => getPrimaryRunOrchestrationIdFromExperimentResult(result) === secondaryAnalysisSourceOrchestrationId
  );

  const experimentResultsToCheck = concat(
    existingParentExperimentResult ? [existingParentExperimentResult] : [],
    parentSecondaryExperimentResults
  );

  const autoInternallyApprove = Boolean(
    some(experimentResultsToCheck, 'approved') || some(existingParentExperimentResult, 'internallyApproved')
  );

  return autoInternallyApprove;
};

export interface SecondaryAnalysisContextModifiers {
  clearSecondaryAnalysisOrchestrationIdQueryParams: () => void;
  setSecondaryAnalysisOrchestrationIdForViewer: (viewerIndex: number, orchestrationId: string) => void;
  setSecondaryAnalysisAreaSelectionMode: (
    newValue: string | ((prev: string) => string),
    updateType?: UrlUpdateType
  ) => void;
}

interface SecondaryAnalysisOrchestration
  extends WebappOrchestration<SecondaryAnalysisContext, SecondaryAnalysisContextModifiers> {}

export const useSecondaryAnalysis = ({
  onSuccess,
  caseId: caseIdFromProps,
  studyId: studyIdFromProps,
  selectedSlides,
  debug,
}: {
  caseId?: number;
  studyId?: string;
  selectedSlides?: SlideWithChannelAndResults[];
  onSuccess?: () => void;
  debug?: boolean;
} = {}): SecondaryAnalysisOrchestration => {
  const { runSecondaryAnalysis, isStartingSecondaryAnalysis } = useRunSecondaryAnalysis({ onSuccess });
  const { hasPermission } = usePermissions();
  const canRunSecondaryAnalysis = hasPermission(Permission.RunSecondaryAnalysis);

  const {
    secondaryAnalysisActiveViewer,
    secondaryAnalysisSourceOrchestrationId,
    setSecondaryAnalysisOrchestrationIdForViewer,
    clearSecondaryAnalysisOrchestrationIdQueryParams,
  } = useSecondaryAnalysisOrchestrationIdQueryParams();

  const { queryParams } = useEncodedFilters();
  const urlParams = useParams();
  const [caseIdFromParam] = useQueryParam('caseId', NumberParam);
  const caseId = caseIdFromProps
    ? caseIdFromProps
    : queryParams.slidesMode
    ? Number(caseIdFromParam)
    : Number(urlParams?.id);

  const studyId =
    studyIdFromProps || first(selectedSlides)?.studyId || urlParams?.studyId || queryParams.filters?.studyId;
  const [secondaryAnalysisAreaSelectionMode, setSecondaryAnalysisAreaSelectionMode] =
    useSecondaryAnalysisSelectionModeQueryParam();

  const slideForActiveViewer = find(selectedSlides, { viewerIndex: secondaryAnalysisActiveViewer });
  const slideId = slideForActiveViewer?.id;

  const secondaryExperimentResult = find(slideForActiveViewer?.experimentResults, {
    orchestrationId: secondaryAnalysisSourceOrchestrationId,
  });

  const wholeSlideAnalysisJob = secondaryExperimentResult?.job as CalculateFeaturesJob | NucleaiExecutorJob;
  const wholeSlideAnalysisJobPrimaryRunOrchestrationId =
    getSecondaryAnalysisJobPrimaryRunOrchestrationId(wholeSlideAnalysisJob);

  if (wholeSlideAnalysisJobPrimaryRunOrchestrationId) {
    console.error(
      'Whole slide analysis job has a primary job - implying it is a secondary analysis itself',
      wholeSlideAnalysisJob?.params
    );
  }

  const wholeSlideAnalysisJobType = wholeSlideAnalysisJob?.type;
  const wholeSlideAnalysisJobParams =
    wholeSlideAnalysisJobType === JobType.PostProcessingAndCalculateFeatures
      ? (wholeSlideAnalysisJob as CalculateFeaturesJob)?.params
      : (wholeSlideAnalysisJob?.params?.graph_params?.[JobType.PostProcessingAndCalculateFeatures]
          ?.params as CalculateFeaturesJob['params']);

  const secondaryAnalysisNotifications = useSecondaryAnalysisNotifications({
    wholeSlideAnalysisJob,
    slideId,
    studyId,
    debug,
  });

  const autoInternallyApprove = Boolean(
    secondaryAnalysisSourceOrchestrationId &&
      shouldAutoInternallyApprove(slideForActiveViewer, secondaryAnalysisSourceOrchestrationId)
  );

  const isSecondaryAnalysisActive =
    Boolean(secondaryAnalysisSourceOrchestrationId) && isNumber(secondaryAnalysisActiveViewer);

  if (debug) {
    console.debug('useSecondaryAnalysis', {
      selectedSlides,
      slideForActiveViewer,
      slideId,
      secondaryExperimentResult,
      secondaryAnalysisSourceOrchestrationId,
      secondaryAnalysisActiveViewer,
      caseId,
      studyId,
      autoInternallyApprove,
      wholeSlideAnalysisJob,
      isSecondaryAnalysisActive,
      canRunSecondaryAnalysis,
      secondaryAnalysisAreaSelectionMode,
    });
  }

  return {
    execution: {
      start: runSecondaryAnalysis,
      isStarting: isStartingSecondaryAnalysis,
    },
    context: {
      isSecondaryAnalysisActive,
      canRunSecondaryAnalysis,
      secondaryAnalysisSourceOrchestrationId,
      caseId,
      studyId,
      slideId,
      autoInternallyApprove,
      wholeSlideAnalysisJob,
      wholeSlideAnalysisJobType,
      wholeSlideAnalysisJobName: wholeSlideAnalysisJob?.name,
      wholeSlideAnalysisJobDescription: wholeSlideAnalysisJob?.description,
      wholeSlideAnalysisJobParams,
      secondaryAnalysisNotifications,
      secondaryAnalysisActiveViewer,
      secondaryAnalysisAreaSelectionMode,
    },
    modifyContext: {
      clearSecondaryAnalysisOrchestrationIdQueryParams,
      setSecondaryAnalysisOrchestrationIdForViewer,
      setSecondaryAnalysisAreaSelectionMode,
    },
  };
};
